import type { ExpressionBuilder, Query } from "@rocicorp/zero";
import type { Schema } from "../schema";
import { FULL_MEMBER_ROLES, ADMIN_PLUS } from "../constants";

// All four guards filter `removed_at IS NULL` on the membership row so a
// soft-deleted (kicked) user does NOT retain access via Zero queries. Zero
// queries on clients only ever see active memberships anyway (publication
// row filter in Step 1b), but Zero runs these same predicates server-side
// at query-auth time — the filter MUST be explicit here.

/**
 * ExpressionBuilder callback for `.where(channelAccessFilter(sub))` on
 * channels queries. Ensures the user is a direct, active member of the
 * channel.
 */
export function channelAccessFilter(sub: string) {
  return (eb: ExpressionBuilder<"channels", Schema>) =>
    eb.exists("members", (mq) =>
      mq.where("user_id", sub).where("removed_at", "IS", null),
    );
}

/**
 * Callback for `.whereExists("channel", channelMemberGuard(sub))` on tables
 * that have a "channel" relationship (messages, channel_members,
 * channel_posting_permissions, pins, etc.).
 */
export function channelMemberGuard(sub: string) {
  return (cq: Query<"channels", Schema>) =>
    cq.whereExists("members", (mq) =>
      mq.where("user_id", sub).where("removed_at", "IS", null),
    );
}

/**
 * Callback for `.whereExists("workspace", workspaceFullMemberGuard(sub))` on
 * tables that have a "workspace" relationship (channels, client_spaces, lists,
 * etc.). Requires FULL_MEMBER_ROLES (excludes collaborators).
 */
export function workspaceFullMemberGuard(sub: string) {
  return (wq: Query<"workspaces", Schema>) =>
    wq.whereExists("members", (mq) =>
      mq
        .where("user_id", sub)
        .where("role", "IN", FULL_MEMBER_ROLES)
        .where("removed_at", "IS", null),
    );
}

/**
 * Like `workspaceFullMemberGuard`, but ADMIN-ONLY in a PUBLIC workspace.
 *
 * Use for workspace MANAGEMENT data that should not REPLICATE into a public-ws
 * drive-by member's Zero client — e.g. `workspace_skills` (prompts + tool
 * bindings). A full member of a PRIVATE workspace keeps access (the Skills /
 * Team admin views are reachable there); in a PUBLIC workspace only ADMIN_PLUS
 * receives the rows. Mirrors `canAccessWorkspaceAdminSurface` on the client and
 * branches 1a/1b of `workspaceMemberRowVisibility`.
 */
export function workspaceAdminSurfaceGuard(sub: string) {
  return (wq: Query<"workspaces", Schema>) =>
    wq.where((eb) =>
      eb.or(
        // Admins see it in any workspace.
        eb.exists("members", (mq) =>
          mq
            .where("user_id", sub)
            .where("role", "IN", ADMIN_PLUS)
            .where("removed_at", "IS", null),
        ),
        // Full members see it only in a NON-public workspace.
        eb.and(
          eb.cmp("is_public", false),
          eb.exists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("role", "IN", FULL_MEMBER_ROLES)
              .where("removed_at", "IS", null),
          ),
        ),
      ),
    );
}

/**
 * Callback for `.whereExists("workspace", workspaceMemberGuard(sub))` on
 * tables that have a "workspace" relationship. Any role (including
 * collaborators).
 */
export function workspaceMemberGuard(sub: string) {
  return (wq: Query<"workspaces", Schema>) =>
    wq.whereExists("members", (mq) =>
      mq.where("user_id", sub).where("removed_at", "IS", null),
    );
}

/**
 * Per-row visibility filter for `workspace_members` queries. Mirrors the
 * branch rule in `workspace_members.byWorkspace`:
 *
 *   1a. ADMINS (ADMIN_PLUS) see ALL members of the workspace — public OR
 *       private.
 *   1b. FULL_MEMBER_ROLES see ALL members ONLY in a NON-public workspace.
 *       In a PUBLIC community anyone can auto-join, so the full roster — and
 *       the member emails it carries — stays admin-only; a drive-by joiner
 *       (role `member`) must NOT be able to enumerate every member. Non-admin
 *       members of a public workspace fall through to branches 2 + 3.
 *   2.  Any requester sees their OWN membership row.
 *   3.  Requester sees members who share a channel with them in this
 *       workspace (so the channel's MemberListPanel etc. show the right
 *       humans).
 *
 * Used both at the top level (`zql.workspace_members.where(...).where(filter)`)
 * AND inside `.related("members", q => q.where(filter))` callbacks on
 * workspace composites — single source of truth so the rule can't drift
 * between the per-table query and the composite's nested relation.
 */
export function workspaceMemberRowVisibility(sub: string, workspaceId: string) {
  return (eb: ExpressionBuilder<"workspace_members", Schema>) =>
    eb.or(
      // Branch 1a: requester is an ADMIN on this workspace → see all rows,
      // public or private.
      eb.exists("workspace", (wq) =>
        wq.whereExists("members", (mq) =>
          mq
            .where("user_id", sub)
            .where("role", "IN", ADMIN_PLUS)
            .where("removed_at", "IS", null),
        ),
      ),
      // Branch 1b: requester is a FULL MEMBER on a NON-public workspace → see
      // all rows. Public workspaces are excluded so the roster + emails stay
      // admin-only there.
      eb.exists("workspace", (wq) =>
        wq
          .where("is_public", false)
          .whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("role", "IN", FULL_MEMBER_ROLES)
              .where("removed_at", "IS", null),
          ),
      ),
      // Branch 2: requester sees own row.
      eb.cmp("user_id", sub),
      // Branch 3: requester sees members who share a channel in this
      // workspace.
      eb.exists("user", (uq) =>
        uq.whereExists("channel_memberships", (cmq) =>
          cmq
            .where("removed_at", "IS", null)
            .whereExists("channel", (cq) =>
              cq
                .where("workspace_id", workspaceId)
                .whereExists("members", (m2) =>
                  m2.where("user_id", sub).where("removed_at", "IS", null),
                ),
            ),
        ),
      ),
    );
}

/**
 * Per-row visibility filter for `workspace_members` queries that span EVERY
 * workspace the requester belongs to (e.g. `allSharedWorkspaceMembers`,
 * used by the Home DM "shared workspace" badge). Unlike
 * `workspaceMemberRowVisibility`, there is no single workspace to pin —
 * each branch correlates to the row's OWN workspace via the
 * `workspace_members → workspace` relationship.
 *
 * Mirrors the self / full-member / channel-peer model in
 * `users.ts:userAccessFilter` — the canonical "can I resolve this user"
 * boundary. Without it, a single-channel collaborator gets the FULL member
 * roster (id, role, joined_at) of every workspace they belong to replicated
 * into their local SQLite (the PR #-era leak fixed here):
 *
 *   1a. ADMIN on the row's OWN workspace → see all of its rows (public or
 *       private).
 *   1b. FULL_MEMBER on the row's OWN NON-public workspace → see all of its
 *       rows. Public workspaces are excluded — see
 *       `workspaceMemberRowVisibility` for why the roster is admin-only there.
 *   2.  Own membership row.
 *   3.  The row's user shares a channel with the requester. The outer query
 *       already restricts to workspaces the requester belongs to, so this
 *       surfaces exactly the contacts the requester can already resolve.
 */
export function workspaceMemberRowVisibilityAcrossWorkspaces(sub: string) {
  return (eb: ExpressionBuilder<"workspace_members", Schema>) =>
    eb.or(
      // Branch 1a: ADMIN on the row's own workspace → see all of its rows
      // (public or private).
      eb.exists("workspace", (wq) =>
        wq.whereExists("members", (mq) =>
          mq
            .where("user_id", sub)
            .where("role", "IN", ADMIN_PLUS)
            .where("removed_at", "IS", null),
        ),
      ),
      // Branch 1b: FULL MEMBER on the row's own NON-public workspace → see all
      // of its rows. Public workspaces are excluded so a drive-by joiner can't
      // harvest the roster/emails of a public community they belong to.
      eb.exists("workspace", (wq) =>
        wq
          .where("is_public", false)
          .whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("role", "IN", FULL_MEMBER_ROLES)
              .where("removed_at", "IS", null),
          ),
      ),
      eb.cmp("user_id", sub),
      eb.exists("user", (uq) =>
        uq.whereExists("channel_memberships", (cmq) =>
          cmq
            .where("removed_at", "IS", null)
            .whereExists("channel", channelMemberGuard(sub)),
        ),
      ),
    );
}
